import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { deactivateOwnProjectMembership, getDashboard } from '@/shared/api/base-api';
import { handleApiError } from '@/shared/api/requests';
import { DashboardData } from '@/types/base';
import { BaseLayout } from '@/shared/components/layout';
import { FEATURE_FLAGS } from '@/features/featureFlags';
import { useFeatureAccess } from '@/shared/hooks/useFeatureAccess';
import { useProjectPermissions } from '@/shared/hooks/useProjectPermissions';
import { useSandboxMode } from '@/features/sandbox';
import {
  Activity,
  BookOpen,
  Building2,
  CalendarCheck,
  Camera,
  CheckSquare,
  ClipboardList,
  FileQuestion,
  FileText,
  FolderOpen,
  Eye,
  Images,
  Layers,
  LayoutDashboard,
  Loader2,
  MapPin,
  ShieldCheck,
  ShieldAlert,
  Settings,
  Users,
  Video,
} from 'lucide-react';
import { useUser } from '@/shared/hooks/useUser';

// Import shadcn/ui components
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/shared/ui/custom/tabs';
import { Card, CardContent } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Label } from '@/shared/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';

// Import types from base instead of defining locally
import { Client, Project, InvitedProject } from '@/types/base';

type DashboardTab = 'active' | 'completed' | 'sandbox';

const DASHBOARD_PROJECT_PERMISSIONS = [
  'view_project_summary',
  'view_project_memberships',
  'manage_project_memberships',
  'can_view_asbuilds',
  'can_view_progress',
  'can_view_drone_videos',
  'can_view_drone_photos',
  'can_view_photo_gallery',
  'can_view_drawings',
  'can_view_punch_lists',
  'can_view_observations',
  'can_view_safety',
  'can_view_rfis',
  'can_view_submittals',
  'can_view_files',
  'can_view_daily_logs',
  'can_view_meetings',
] as const;

const ProjectCard: React.FC<{ 
  project: Project | InvitedProject; 
  client: Client; 
  isInvited?: boolean;
  isPunchListsFeatureEnabled?: boolean;
  isRfisFeatureEnabled?: boolean;
  isDrawingsFeatureEnabled?: boolean;
  isObservationsFeatureEnabled?: boolean;
  isSafetyFeatureEnabled?: boolean;
  isPhotoGalleryFeatureEnabled?: boolean;
  isSubmittalsFeatureEnabled?: boolean;
  isFilesFeatureEnabled?: boolean;
  isMeetingMinutesFeatureEnabled?: boolean;
  isDailyLogFeatureEnabled?: boolean;
  canSeeClientAdmin?: boolean;
}> = ({
  project,
  client,
  isInvited = false,
  isPunchListsFeatureEnabled = false,
  isRfisFeatureEnabled = false,
  isDrawingsFeatureEnabled = false,
  isObservationsFeatureEnabled = false,
  isSafetyFeatureEnabled = false,
  isPhotoGalleryFeatureEnabled = false,
  isSubmittalsFeatureEnabled = false,
  isFilesFeatureEnabled = false,
  isMeetingMinutesFeatureEnabled = false,
  isDailyLogFeatureEnabled = false,
  canSeeClientAdmin = false,
}) => {
  const navigate = useNavigate();
  const [thumbnailError, setThumbnailError] = useState(false);
  const { permissions: projectPermissions } = useProjectPermissions(
    project,
    DASHBOARD_PROJECT_PERMISSIONS,
  );
  const hasProjectValue = (value?: string | null) =>
    Boolean(value && value.trim() !== '' && value.trim().toLowerCase() !== 'none');

  const canAccessProjectSummary = projectPermissions.view_project_summary;
  const canAccessTeam = projectPermissions.view_project_memberships;
  const canManageProject = projectPermissions.manage_project_memberships;
  const canAccessProgress = hasProjectValue(project.progress_directory) && projectPermissions.can_view_progress;
  const canAccessAsbuilds = hasProjectValue(project.asbuilds_directory) && projectPermissions.can_view_asbuilds;
  const canAccessDrones = hasProjectValue(project.drones_directory) && projectPermissions.can_view_drone_videos;
  const canAccessDronePhotos = hasProjectValue(project.drone_photos_link) && projectPermissions.can_view_drone_photos;
  const canAccessPhotoGallery = isPhotoGalleryFeatureEnabled && projectPermissions.can_view_photo_gallery;
  const canAccessDrawings = isDrawingsFeatureEnabled && projectPermissions.can_view_drawings;
  const canAccessPunchLists = isPunchListsFeatureEnabled && projectPermissions.can_view_punch_lists;
  const canAccessObservations = isObservationsFeatureEnabled && projectPermissions.can_view_observations;
  const canAccessSafety = isSafetyFeatureEnabled && projectPermissions.can_view_safety;
  const canAccessRfis = isRfisFeatureEnabled && projectPermissions.can_view_rfis;
  const canAccessSubmittals = isSubmittalsFeatureEnabled && projectPermissions.can_view_submittals;
  const canAccessFiles = isFilesFeatureEnabled && projectPermissions.can_view_files;
  const canAccessDailyLog = isDailyLogFeatureEnabled && projectPermissions.can_view_daily_logs;
  const canAccessMeetingMinutes = isMeetingMinutesFeatureEnabled && projectPermissions.can_view_meetings;

  const ActionChip: React.FC<{
    href?: string; 
    isActive: boolean; 
    children: React.ReactNode;
    external?: boolean;
    icon?: React.ReactNode;
  }> = ({ href, isActive, children, external = false, icon }) => {
    const chipClass = `inline-flex items-center gap-2.5 rounded-[11px] border border-border bg-muted px-3.5 py-2.5 text-[13.5px] font-semibold text-foreground transition-colors duration-150 ${
      isActive
        ? 'hover:border-primary hover:bg-primary/12 hover:ring-[3px] hover:ring-primary/15 hover:[&_svg]:text-primary'
        : 'pointer-events-none cursor-not-allowed opacity-40 grayscale'
    }`;
    const chipContent = (
      <>
        {icon && <span className="flex h-4 w-4 items-center text-muted-foreground transition-colors [&_svg]:h-4 [&_svg]:w-4 [&_svg]:stroke-[1.9]">{icon}</span>}
        {children}
      </>
    );
    
    if (!isActive || !href) {
      return <span className={chipClass}>{chipContent}</span>;
    }
    
    if (external) {
      return (
        <a 
          href={href} 
          target="_blank" 
          rel="noopener noreferrer" 
          className={chipClass}
        >
          {chipContent}
        </a>
      );
    }
    
    return (
      <button
        type="button"
        onClick={() => navigate(href)}
        className={chipClass}
      >
        {chipContent}
      </button>
    );
  };

  const featureGroups = [
    {
      label: 'General',
      items: [
        {
          label: 'Project Dashboard',
          href: canAccessProjectSummary
            ? `/app/property/${client.name}/${project.id}/dashboard`
            : undefined,
          isActive: canAccessProjectSummary,
          icon: <LayoutDashboard />,
        },
      ],
    },
    {
      label: 'Ironview 360',
      items: [
        {
          label: 'In Progress',
          href: canAccessProgress
            ? `/app/property/${client.name}/${project.id}/progress`
            : undefined,
          isActive: canAccessProgress,
          icon: <Activity className="h-3.5 w-3.5" />,
        },
        {
          label: 'As Build',
          href: canAccessAsbuilds
            ? `/app/property/${client.name}/${project.id}/asbuilds`
            : undefined,
          isActive: canAccessAsbuilds,
          icon: <BookOpen />,
        },
        {
          label: 'Post-Tension Decks',
          href: canAccessDrones
            ? `/app/property/${client.name}/${project.id}/drones`
            : undefined,
          isActive: canAccessDrones,
          icon: <Layers />,
        },
        {
          label: 'Drone Photos',
          href: canAccessDronePhotos ? project.drone_photos_link : undefined,
          isActive: canAccessDronePhotos,
          external: true,
          icon: <Camera />,
        },
        {
          label: 'Photo Gallery',
          href: canAccessPhotoGallery
            ? `/app/property/${client.name}/${project.id}/photos`
            : undefined,
          isActive: canAccessPhotoGallery,
          icon: <Images />,
        },
      ],
    },
    {
      label: 'Construction Management',
      items: [
        {
          label: 'Drawings',
          href: canAccessDrawings
            ? `/app/property/${client.name}/${project.id}/drawings`
            : undefined,
          isActive: canAccessDrawings,
          icon: <FileText />,
        },
        {
          label: 'Punch Lists',
          href: canAccessPunchLists
            ? `/app/property/${client.name}/${project.id}/punch-lists`
            : undefined,
          isActive: canAccessPunchLists,
          icon: <CheckSquare />,
        },
        {
          label: 'Observations',
          href: canAccessObservations
            ? `/app/property/${client.name}/${project.id}/observations`
            : undefined,
          isActive: canAccessObservations,
          icon: <Eye />,
        },
        {
          label: 'Safety',
          href: canAccessSafety
            ? `/app/property/${client.name}/${project.id}/safety`
            : undefined,
          isActive: canAccessSafety,
          icon: <ShieldAlert />,
        },
        {
          label: 'RFIs',
          href: canAccessRfis
            ? `/app/property/${client.name}/${project.id}/rfis`
            : undefined,
          isActive: canAccessRfis,
          icon: <FileQuestion />,
        },
        {
          label: 'Submittals',
          href: canAccessSubmittals
            ? `/app/property/${client.name}/${project.id}/submittals`
            : undefined,
          isActive: canAccessSubmittals,
          icon: <ClipboardList />,
        },
        {
          label: 'Files',
          href: canAccessFiles
            ? `/app/property/${client.name}/${project.id}/files`
            : undefined,
          isActive: canAccessFiles,
          icon: <FolderOpen />,
        },
        {
          label: 'Daily Log',
          href: canAccessDailyLog
            ? `/app/property/${client.name}/${project.id}/daily-log`
            : undefined,
          isActive: canAccessDailyLog,
          icon: <CalendarCheck />,
        },
      ],
    },
    {
      label: 'Personnel Management',
      items: [
        ...(!isInvited
          ? [{
              label: 'Team',
              href: canAccessTeam ? `/app/team/${client.name}/${project.name}` : undefined,
              isActive: canAccessTeam,
              icon: <Users />,
            }]
          : []),
        {
          label: 'Meetings',
          href: canAccessMeetingMinutes
            ? `/app/property/${client.name}/${project.id}/meeting-minutes`
            : undefined,
          isActive: canAccessMeetingMinutes,
          icon: <Video />,
        },
        ...(canSeeClientAdmin
          ? [{
              label: 'Administration',
              href: `/app/admin/${client.name}`,
              isActive: true,
              icon: <ShieldCheck />,
            }]
          : []),
      ],
    },
  ];

  const thumbnailSrc = project.thumbnail || project.thumbnail_url || '';
  return (
    <Card className="relative grid overflow-hidden md:grid-cols-[0.92fr_1.08fr]">
      {canManageProject && (
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="absolute right-4 top-4 z-10 size-10 border-border bg-background/85 text-muted-foreground shadow-sm backdrop-blur-md hover:text-primary"
          aria-label={`Open settings for ${project.name}`}
          title="Project settings"
          onClick={() => navigate(`/app/property/${client.name}/${project.id}/settings`)}
        >
          <Settings className="size-4" strokeWidth={1.9} />
        </Button>
      )}
      <div className="relative min-h-[230px] overflow-hidden bg-[#10131b] md:min-h-[430px]">
        {thumbnailSrc && !thumbnailError ? (
          <a
            href={thumbnailSrc}
            data-lightbox={`${project.name}-box`}
            data-title={project.name}
            className="block h-full w-full"
          >
            <img
              src={thumbnailSrc}
              alt={`${project.name} thumbnail`}
              className="absolute inset-0 h-full w-full object-cover"
              onError={() => setThumbnailError(true)}
            />
          </a>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-muted">
            <Building2 className="h-12 w-12 text-muted-foreground/60" strokeWidth={1.9} />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent from-[55%] to-card md:bg-gradient-to-r" />
      </div>

      <div className="px-[34px] pb-[34px] pt-[30px]">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-display text-[1.875rem] font-extrabold tracking-[-0.02em]">
            {project.name}
          </h2>
          {isInvited && <Badge variant="outline">Invited</Badge>}
        </div>

        {project.address && project.address !== "None" && (
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(project.address)}`}
            className="my-[11px] inline-flex max-w-full items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
            target="_blank"
            rel="noopener noreferrer"
          >
            <MapPin className="h-4 w-4 shrink-0 text-primary" strokeWidth={1.9} />
            <span className="truncate">{project.address}</span>
          </a>
        )}

        {featureGroups.map((group) => (
          <div key={group.label} className="mt-[22px]">
            <h3 className="mb-[11px] flex items-center gap-2.5 text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
              <span>{group.label}</span>
              <span className="h-px flex-1 bg-border" />
            </h3>
            <div className="flex flex-wrap gap-2.5">
              {group.items.map((item) => (
                <ActionChip
                  key={item.label}
                  href={item.href}
                  isActive={item.isActive}
                  external={item.external}
                  icon={item.icon}
                >
                  {item.label}
                </ActionChip>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

const getInitialDashboardTab = (data: DashboardData): DashboardTab => {
  const projects = [...data.projects, ...data.invited_projects];
  if (projects.some((p) => !p.is_sandbox && p.is_active)) return 'active';
  if (projects.some((p) => !p.is_sandbox && !p.is_active)) return 'completed';
  if (projects.some((p) => p.is_sandbox)) return 'sandbox';
  return 'active';
};

const Dashboard: React.FC = () => {
  const { clientName } = useParams<{ clientName: string }>();
  const { user } = useUser();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DashboardTab>('active');
  const [manageProjectsOpen, setManageProjectsOpen] = useState(false);
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<number>>(new Set());
  const [isLeaving, setIsLeaving] = useState(false);
  const [leaveError, setLeaveError] = useState<string | null>(null);

  const punchListsAccess = useFeatureAccess(FEATURE_FLAGS.PUNCH_LISTS);
  const rfisAccess = useFeatureAccess(FEATURE_FLAGS.RFIS);
  const drawingsAccess = useFeatureAccess(FEATURE_FLAGS.DRAWINGS);
  const observationsAccess = useFeatureAccess(FEATURE_FLAGS.OBSERVATIONS);
  const safetyAccess = useFeatureAccess(FEATURE_FLAGS.SAFETY);
  const photoGalleryAccess = useFeatureAccess(FEATURE_FLAGS.PHOTO_GALLERY);
  const submittalsAccess = useFeatureAccess(FEATURE_FLAGS.SUBMITTALS);
  const filesAccess = useFeatureAccess(FEATURE_FLAGS.FILES);
  const meetingMinutesAccess = useFeatureAccess(FEATURE_FLAGS.MEETING_MINUTES);
  const dailyLogAccess = useFeatureAccess(FEATURE_FLAGS.DAILY_LOG);
  const { inSandboxMode, sandbox, maybeAutoEnter } = useSandboxMode();

  const fetchDashboardData = useCallback(async () => {
    if (!clientName) return;

    try {
      const data = await getDashboard(clientName);
      setDashboardData(data);
      setActiveTab(getInitialDashboardTab(data));
    } catch (err: unknown) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [clientName]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Auto-enter sandbox mode on first dashboard load IF the user's client
  // has a sandbox AND no real (non-sandbox) projects to look at. This is
  // gated by a one-shot session sentinel inside `useSandboxMode`, so an
  // explicit user `exit()` afterwards is not undone by a re-render.
  useEffect(() => {
    if (!dashboardData) return;
    if (!sandbox) return;
    const allProjects = [
      ...dashboardData.projects,
      ...dashboardData.invited_projects,
    ];
    const hasNonSandboxProjects = allProjects.some((p) => !p.is_sandbox);
    maybeAutoEnter({ hasNonSandboxProjects });
  }, [dashboardData, sandbox, maybeAutoEnter]);

  // Handle all non-success states with BaseLayout
  if (loading || error || !dashboardData) {
    return (
      <BaseLayout 
        isLoading={loading}
        loadingMessage="Loading your dashboard..."
      >
        {error && (
          <div className="min-h-screen bg-background flex items-center justify-center">
            <div className="text-destructive text-lg">Error: {error}</div>
          </div>
        )}
        {!loading && !error && !dashboardData && (
          <div className="min-h-screen bg-background flex items-center justify-center">
            <div className="text-lg text-muted-foreground">No dashboard data available</div>
          </div>
        )}
      </BaseLayout>
    );
  }

  const allProjects = [...dashboardData.projects, ...dashboardData.invited_projects];
  const nonSandboxProjects = allProjects.filter((p) => !p.is_sandbox);
  const sandboxProjects = allProjects.filter((p) => p.is_sandbox);
  const activeProjects = nonSandboxProjects.filter(p => p.is_active);
  const completedProjects = nonSandboxProjects.filter(p => !p.is_active);
  const manageableProjects = nonSandboxProjects.filter((p) => p.membership_id != null);
  const canSeeClientAdmin = Boolean(user?.is_staff || user?.is_superuser || user?.is_client_admin);

  const openManageProjects = () => {
    setSelectedProjectIds(new Set());
    setLeaveError(null);
    setManageProjectsOpen(true);
  };

  const toggleProjectSelection = (projectId: number) => {
    setSelectedProjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  const toggleAllProjects = () => {
    if (selectedProjectIds.size === manageableProjects.length) {
      setSelectedProjectIds(new Set());
    } else {
      setSelectedProjectIds(new Set(manageableProjects.map((p) => p.id)));
    }
  };

  const handleLeaveSelectedProjects = async () => {
    if (selectedProjectIds.size === 0) return;

    setIsLeaving(true);
    setLeaveError(null);

    const ids = Array.from(selectedProjectIds);
    const results = await Promise.allSettled(
      ids.map((projectId) => deactivateOwnProjectMembership(projectId)),
    );

    const failures = results.filter((r) => r.status === 'rejected');
    setIsLeaving(false);

    if (failures.length > 0) {
      const firstError = failures[0];
      setLeaveError(
        firstError.status === 'rejected'
          ? handleApiError(firstError.reason)
          : 'Some projects could not be removed. Please try again.',
      );
      if (failures.length < ids.length) {
        setLoading(true);
        await fetchDashboardData();
        setSelectedProjectIds(new Set());
      }
      return;
    }

    setManageProjectsOpen(false);
    setSelectedProjectIds(new Set());
    setLoading(true);
    await fetchDashboardData();
  };

  const renderProjectList = (projects: (Project | InvitedProject)[]) => (
    <div className="grid gap-5">
      {projects.map((project) => {
        const isInvited = dashboardData.invited_projects.some((p) => p.id === project.id);
        return (
          <ProjectCard
            key={project.id}
            project={project}
            client={dashboardData.client}
            isInvited={isInvited}
            isPunchListsFeatureEnabled={punchListsAccess.isEnabled}
            isRfisFeatureEnabled={rfisAccess.isEnabled}
            isDrawingsFeatureEnabled={drawingsAccess.isEnabled}
            isObservationsFeatureEnabled={observationsAccess.isEnabled}
            isSafetyFeatureEnabled={safetyAccess.isEnabled}
            isPhotoGalleryFeatureEnabled={photoGalleryAccess.isEnabled}
            isSubmittalsFeatureEnabled={submittalsAccess.isEnabled}
            isFilesFeatureEnabled={filesAccess.isEnabled}
            isMeetingMinutesFeatureEnabled={meetingMinutesAccess.isEnabled}
            isDailyLogFeatureEnabled={dailyLogAccess.isEnabled}
            canSeeClientAdmin={canSeeClientAdmin}
          />
        );
      })}
    </div>
  );

  const dashboardCrumb = inSandboxMode && sandbox
    ? `${dashboardData.client.name} Sandbox`
    : dashboardData.client.name;

  return (
    <BaseLayout>
      <div className="mx-auto w-full max-w-[1600px] px-4 py-8 sm:px-6 lg:px-9">
        <div className="mb-[22px] flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[13.5px] font-semibold text-muted-foreground">{dashboardCrumb}</div>
            <h1 className="truncate font-display text-3xl font-extrabold tracking-[-0.02em] md:text-[2.4rem]">
              Projects
            </h1>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as DashboardTab)}>
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <TabsList className="w-fit">
              <TabsTrigger value="active">
                Active<span className="ml-1">({activeProjects.length})</span>
              </TabsTrigger>
              <TabsTrigger value="completed">
                Completed<span className="ml-1">({completedProjects.length})</span>
              </TabsTrigger>
              <TabsTrigger value="sandbox">
                Sandbox<span className="ml-1">({sandboxProjects.length})</span>
              </TabsTrigger>
            </TabsList>
            {manageableProjects.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2 text-muted-foreground hover:text-foreground"
                aria-label="Manage project access"
                onClick={openManageProjects}
              >
                <Settings className="h-4 w-4" />
                Manage Access
              </Button>
            )}
          </div>

            <TabsContent value="active" className="mt-0">
              {activeProjects.length === 0 ? (
                <Card>
                  <CardContent className="py-16 text-center">
                    <p className="text-sm text-muted-foreground">
                      0 active projects and {completedProjects.length} completed projects
                    </p>
                  </CardContent>
                </Card>
              ) : (
                renderProjectList(activeProjects)
              )}
            </TabsContent>

            <TabsContent value="completed" className="mt-0">
              {completedProjects.length === 0 ? (
                <Card>
                  <CardContent className="py-16 text-center">
                    <p className="text-sm text-muted-foreground">
                      {activeProjects.length} active projects and 0 completed projects
                    </p>
                  </CardContent>
                </Card>
              ) : (
                renderProjectList(completedProjects)
              )}
            </TabsContent>

            <TabsContent value="sandbox" className="mt-0">
              {sandboxProjects.length === 0 ? (
                <Card>
                  <CardContent className="py-16 text-center">
                    <p className="text-sm text-muted-foreground">
                      No sandbox project is available for this client.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                renderProjectList(sandboxProjects)
              )}
            </TabsContent>
          </Tabs>

          <Dialog
            open={manageProjectsOpen}
            onOpenChange={(open) => {
              setManageProjectsOpen(open);
              if (!open) {
                setSelectedProjectIds(new Set());
                setLeaveError(null);
              }
            }}
          >
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Manage project access</DialogTitle>
                <DialogDescription>
                  Select the projects you want to remove From this dashboard. WARNING: You will lose access
                  until a project administrator adds you back, or you accept a new invitation.
                </DialogDescription>
              </DialogHeader>

              {manageableProjects.length === 0 ? (
                <p className="text-sm text-muted-foreground">No projects available to manage.</p>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 border-b border-border pb-3">
                    <Checkbox
                      id="select-all-projects"
                      checked={
                        manageableProjects.length > 0 &&
                        selectedProjectIds.size === manageableProjects.length
                      }
                      onCheckedChange={toggleAllProjects}
                    />
                    <Label htmlFor="select-all-projects" className="cursor-pointer font-medium">
                      Select all ({manageableProjects.length})
                    </Label>
                  </div>

                  <div className="max-h-[min(50vh,320px)] space-y-2 overflow-y-auto pr-1">
                    {manageableProjects.map((project) => {
                      const checkboxId = `leave-project-${project.id}`;
                      const isInvited = dashboardData.invited_projects.some(
                        (p) => p.id === project.id,
                      );
                      return (
                        <label
                          key={project.id}
                          htmlFor={checkboxId}
                          className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 hover:bg-muted/50"
                        >
                          <Checkbox
                            id={checkboxId}
                            checked={selectedProjectIds.has(project.id)}
                            onCheckedChange={() => toggleProjectSelection(project.id)}
                          />
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-foreground">{project.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {project.is_active ? 'Active project' : 'Completed project'}
                              {isInvited ? ' · Invited member' : ''}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {leaveError && <p className="text-sm text-destructive">{leaveError}</p>}

              <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
                <p className="text-xs text-muted-foreground sm:mr-auto sm:self-center">
                  {selectedProjectIds.size === 0
                    ? 'Select at least one project'
                    : `${selectedProjectIds.size} project${selectedProjectIds.size === 1 ? '' : 's'} selected`}
                </p>
                <div className="flex w-full gap-2 sm:w-auto">
                  <Button
                    variant="outline"
                    onClick={() => setManageProjectsOpen(false)}
                    disabled={isLeaving}
                    className="flex-1 sm:flex-none"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleLeaveSelectedProjects}
                    disabled={isLeaving || selectedProjectIds.size === 0}
                    className="flex-1 sm:flex-none"
                  >
                    {isLeaving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Leaving...
                      </>
                    ) : (
                      `Leave selected (${selectedProjectIds.size})`
                    )}
                  </Button>
                </div>
              </DialogFooter>
            </DialogContent>
          </Dialog>
      </div>
    </BaseLayout>
  );
};

export default Dashboard;
